/**
 * AccessibilityPage — public WCAG 2.1 AA conformance statement.
 *
 * Why this page exists:
 *   - ADA case law (e.g. Robles v. Domino's, Gil v. Winn-Dixie) treats
 *     a published accessibility statement + a working contact channel
 *     as material defensibility evidence.
 *   - Google Play and Apple App Store reviewers increasingly look for
 *     accessibility documentation linked from privacy/support surfaces.
 *   - It signals to disabled users (and their advocacy networks) that
 *     we take their experience seriously, which materially reduces the
 *     risk of being targeted by ADA-tester demand letters.
 *
 * Hosting note: publicly reachable without auth, like /privacy. Linked
 * from the Privacy page footer, the Welcome page footer, and Profile.
 *
 * IMPORTANT — placeholder copy: update COMPANY_NAME and CONTACT_EMAIL
 * to match your legal entity + real support address before publishing.
 * The structure follows the W3C Accessibility Statement Generator
 * recommended layout (https://www.w3.org/WAI/planning/statements/).
 */
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'April 29, 2026';
const COMPANY_NAME = 'Crowsearinv';                  // ⚠️ Replace with legal entity
const CONTACT_EMAIL = 'accessibility@lingoflow.app'; // ⚠️ Replace with real address

export default function AccessibilityPage() {
  return (
    <div className="lf-shell px-6 py-8 max-w-2xl mx-auto" data-testid="accessibility-page">
      <Link
        to="/"
        className="text-xs text-[var(--lf-text-muted)] underline-offset-2 hover:underline"
        data-testid="accessibility-home-link"
      >
        ← Back to LingoFlow
      </Link>

      <h1 className="mt-3 text-3xl font-bold">Accessibility Statement</h1>
      <p className="mt-1 text-xs text-[var(--lf-text-muted)]">
        Last updated: {LAST_UPDATED}
      </p>

      <Section>
        <P>
          {COMPANY_NAME} is committed to making LingoFlow usable by everyone, including
          people with disabilities. We design and build with the{' '}
          <a
            href="https://www.w3.org/TR/WCAG21/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--lf-primary)] underline"
          >
            Web Content Accessibility Guidelines (WCAG) 2.1
          </a>{' '}
          as our reference standard, and we treat accessibility as an ongoing
          engineering discipline — not a one-time audit.
        </P>
      </Section>

      <H2>Conformance status</H2>
      <Section>
        <P>
          LingoFlow is designed to conform to <strong>WCAG 2.1 Level AA</strong>.
          "Conform" means the app meets the requirements; "designed to" means we
          actively monitor and remediate gaps as we discover them.
        </P>
        <P className="mt-2">
          When packaged as a native iOS or Android app via Capacitor, the same
          accessibility behaviors apply, including support for VoiceOver
          (iOS) and TalkBack (Android) screen readers.
        </P>
      </Section>

      <H2>What we have done</H2>
      <Section>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong>Screen-reader support.</strong> Both the conversation
            transcript and the Teacher Mode lesson view are announced live as
            the AI responds (<code>aria-live</code> with role <code>log</code>),
            so blind learners hear the AI's reply text in real time.
          </li>
          <li>
            <strong>Always-visible captions for AI audio.</strong> Every spoken
            response from the AI tutor appears as text in the transcript at the
            same time. Deaf and hard-of-hearing learners do not depend on TTS
            audio to follow the conversation.
          </li>
          <li>
            <strong>Color contrast ≥ 4.5:1.</strong> Body text (≈ 13:1), the
            warm-orange primary color (5.6:1), and muted secondary text
            (5.7:1) all clear the WCAG AA threshold.
          </li>
          <li>
            <strong>Keyboard navigation.</strong> Every interactive element is
            reachable via Tab, has a visible focus ring, and a "Skip to content"
            link is the first thing a keyboard user encounters on every page.
          </li>
          <li>
            <strong>Form labels.</strong> Every input has either a visible label
            or a programmatically-associated hidden label, and the right{' '}
            <code>autocomplete</code> hints so password managers and assistive
            tech work as expected.
          </li>
          <li>
            <strong>Reduced-motion support.</strong> If your device is set to
            "Reduce Motion," the app skips non-essential animations and disables
            the auto-rotating welcome carousel.
          </li>
          <li>
            <strong>Tap-target sizing.</strong> Interactive controls are sized
            to meet WCAG 2.5.5 (24×24 px minimum) and most use the recommended
            44×44 px target for primary actions.
          </li>
          <li>
            <strong>Plain-language UI copy.</strong> We write at roughly an 8th-grade
            reading level so the app is approachable for learners using English
            (or Spanish) as a second language — and for learners with cognitive
            disabilities.
          </li>
        </ul>
      </Section>

      <H2>Known limitations</H2>
      <Section>
        <P>
          We are honest about the spots where we are still working:
        </P>
        <ul className="list-disc pl-5 space-y-1.5 mt-2">
          <li>
            <strong>Voice-first nature of the product.</strong> Spoken practice
            is a core feature. Learners who cannot use a microphone can still
            follow lessons and read transcripts, but the speaking-practice
            scoring loop requires audio input.
          </li>
          <li>
            <strong>Third-party content.</strong> Some hero photography is
            served via Unsplash; if their CDN ever fails to load, our fallback
            is a simple background — but the surrounding text remains readable
            without the image.
          </li>
          <li>
            <strong>PayPal checkout window.</strong> Payment flow runs inside
            PayPal's hosted checkout; their accessibility level is set by
            PayPal, not by us.
          </li>
          <li>
            <strong>WCAG 2.1 Level AAA</strong> is not a current target. We meet
            many AAA criteria incidentally (e.g. body-text contrast clears
            13:1) but we do not formally claim AAA conformance.
          </li>
        </ul>
      </Section>

      <H2>Assistive-tech compatibility</H2>
      <Section>
        <P>
          The app is regularly tested with the following combinations:
        </P>
        <ul className="list-disc pl-5 space-y-1.5 mt-2">
          <li>NVDA + Firefox (Windows)</li>
          <li>VoiceOver + Safari (macOS, iOS)</li>
          <li>TalkBack + Chrome (Android)</li>
          <li>Keyboard-only navigation (no mouse, no touchscreen)</li>
          <li>Browser-level zoom up to 200%</li>
        </ul>
      </Section>

      <H2>Tell us when something doesn't work</H2>
      <Section>
        <P>
          If you hit a wall — a button you can't reach, copy you can't read, an
          announcement that doesn't come through — please write to us. We treat
          accessibility reports as urgent.
        </P>
        <p className="mt-2">
          Email:{' '}
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Accessibility%20feedback`}
            className="text-[var(--lf-primary)] underline font-semibold"
            data-testid="accessibility-contact-email"
          >
            {CONTACT_EMAIL}
          </a>
        </p>
        <P className="mt-2">
          We aim to acknowledge every report within <strong>2 business days</strong> and
          to provide a remediation timeline within <strong>10 business days</strong>.
          Wherever possible, fixes ship in the next release cycle.
        </P>
      </Section>

      <H2>Formal complaints</H2>
      <Section>
        <P>
          If you have made a good-faith effort to reach us at the email above
          and have not received an adequate response, you may also file a
          complaint with the U.S. Department of Justice under the Americans with
          Disabilities Act (ADA), or with the equivalent national body in your
          jurisdiction.
        </P>
      </Section>

      <H2>How we test</H2>
      <Section>
        <P>
          Our accessibility checks are part of every release:
        </P>
        <ul className="list-disc pl-5 space-y-1.5 mt-2">
          <li>
            Automated DOM audits run during CI and on each preview build —
            covering label association, contrast, ARIA usage, focus order, and
            tap-target sizing.
          </li>
          <li>
            Manual screen-reader walkthroughs of the core flows
            (sign-up, onboarding, live session, post-session review).
          </li>
          <li>
            Periodic third-party audits against the latest WCAG criteria.
          </li>
        </ul>
      </Section>

      <div className="mt-10 mb-8 text-xs text-[var(--lf-text-muted)] lf-bottom-safe">
        <p>
          See also:{' '}
          <Link to="/privacy" className="underline">Privacy Policy</Link>{' '}
          ·{' '}
          <Link to="/" className="underline">Home</Link>
        </p>
        <p className="mt-2">
          © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
        </p>
      </div>
    </div>
  );
}

function H2({ children }) {
  return (
    <h2 className="mt-7 text-lg font-bold border-b border-[var(--lf-border)] pb-1">
      {children}
    </h2>
  );
}

function Section({ children }) {
  return <div className="mt-3 space-y-2 text-sm leading-relaxed">{children}</div>;
}

function P({ children, className = '' }) {
  return <p className={className}>{children}</p>;
}
